/** 조사 — judge any keyword before it is tracked (S4, `/research`).
 *
 *  One term and a storefront go in. Out come Apple's popularity or the
 *  autocomplete standing in for it, difficulty, the store's own top results with
 *  who holds the phrase in the name, the autocomplete list, the Search API's ten
 *  apps, and the server's opportunity tier. "앱에 추가" drops the term into one
 *  of my apps as a tracked keyword. Below sits the name check.
 *
 *  The term and the country live in the address (`?term=&country=`), so a
 *  researched keyword survives a reload and can be linked. The address only
 *  fills the form: opening or reloading it never calls out. 분석 goes out to
 *  iTunes and the App Store, so only the button runs it. A result already fetched in
 *  this session (same term and country) is read back from the query cache. */

import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { api, type ITunesResult, type ResearchResult, type StoreApp } from '@/api/client'
import { demandLine } from '@/lib/demand'
import { OPPORTUNITY_MEANING } from '@/lib/opportunity'
import { invalidateKeywordData, useApps } from '@/lib/queries'
import { difficultyBand, statusOf, TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  Button,
  CountryTag,
  DataTable,
  EmptyState,
  errorText,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatTile,
  TierPill,
  useSetQueryParams,
  useToast,
  type Column,
} from '@/ui'
import { NameCheck } from './NameCheck'

export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return `${n}`
}

const RESEARCH_STALE_MS = 30 * 60_000
const researchKey = (term: string, country: string) => ['research', term, country] as const

const cleanCountry = (v: string) => v.trim().toLowerCase().slice(0, 2)

export default function Research() {
  const toast = useToast()
  const setParams = useSetQueryParams()
  const [params] = useSearchParams()
  const term = (params.get('term') ?? '').trim()
  const country = cleanCountry(params.get('country') ?? '') || 'us'

  const [draft, setDraft] = useState(term)
  const [draftCountry, setDraftCountry] = useState(country)
  // Back/forward changes the address; the form follows it.
  useEffect(() => {
    setDraft(term)
    setDraftCountry(country)
  }, [term, country])

  // Disabled: the query only reads the cache and mirrors the state of a fetch
  // that `run` started. Nothing calls out on mount, reload or focus.
  const qc = useQueryClient()
  const search = useQuery<ResearchResult>({
    queryKey: researchKey(term, country),
    queryFn: () => api.research(term, country),
    enabled: false,
    retry: false,
    staleTime: RESEARCH_STALE_MS,
    gcTime: RESEARCH_STALE_MS,
  })

  // A failure is shown in place and as a toast, once per failure.
  const failure = search.error
  useEffect(() => {
    if (failure) toast.error(errorText(failure))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failure])

  const run = (e?: FormEvent) => {
    e?.preventDefault()
    const t = draft.trim()
    if (!t || search.isFetching) return
    const c = cleanCountry(draftCountry) || 'us'
    if (t !== term || c !== country) setParams({ term: t, country: c })
    // staleTime 0: pressing the button always asks again, even for a cached term.
    qc.fetchQuery({ queryKey: researchKey(t, c), queryFn: () => api.research(t, c), staleTime: 0, retry: false }).catch(() => {
      // the failure is on the query; the page shows it
    })
  }

  const analyzing = term !== '' && search.isFetching
  const result = search.data

  return (
    <>
      <PageHeader title="조사" description="앱에 넣기 전에 키워드의 수요, 난이도와 상위 앱을 본다.">
        <form onSubmit={run} className="flex flex-wrap items-end gap-2" role="search" aria-label="키워드 조사">
          <Field label="키워드" className="min-w-[200px] flex-1">
            <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="habit tracker" />
          </Field>
          <Field label="국가" className="w-20">
            <Input
              value={draftCountry}
              onChange={(e) => setDraftCountry(e.target.value.toLowerCase())}
              maxLength={2}
              placeholder="us"
              title="스토어 국가 코드 (us, kr, jp)"
              className="uppercase"
            />
          </Field>
          <Button type="submit" variant="primary" icon={Search} loading={analyzing} disabled={!draft.trim()}>
            {analyzing ? '분석 중' : '분석'}
          </Button>
        </form>
      </PageHeader>

      {search.isError ? (
        <ErrorState title="분석하지 못했다" error={search.error} onRetry={() => search.refetch()} />
      ) : analyzing && !result ? (
        <Skeleton className="h-64 w-full" />
      ) : result ? (
        <Results result={result} />
      ) : (
        <EmptyState
          icon={Search}
          title="키워드를 넣고 분석을 누른다"
          description="인기도, 난이도, 자동완성과 스토어 상위 앱을 보여 준다. 앱에 넣지 않아도 된다."
        />
      )}
      <NameCheck country={country} />
    </>
  )
}

function Results({ result }: { result: ResearchResult }) {
  const diff = statusOf('difficulty', difficultyBand(result.difficulty))
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="min-w-0 break-words text-section font-semibold">“{result.term}”</h2>
          <CountryTag code={result.country} />
          <TierPill tier={result.opportunity} />
        </div>
        <AddToApp term={result.term} country={result.country} />
      </div>
      <p className="-mt-2 text-table text-muted-foreground">{OPPORTUNITY_MEANING[result.opportunity] ?? OPPORTUNITY_MEANING.unscored}</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="인기도" value={result.popularity} hint={result.demand && demandLine(result.demand)} />
        <StatTile label="난이도" value={result.difficulty} tone={diff.tone === 'published' ? undefined : diff.tone} hint={diff.label} />
        <StatTile label="결과 수" value={result.results_count.toLocaleString('ko-KR')} />
      </div>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4" aria-label="인기도와 난이도">
        <Measure label="인기도" value={result.popularity} fill="bg-chart-1" />
        <Measure label="난이도" value={result.difficulty} fill={TONE_CLASS[diff.tone].dot} />
        <p className="pt-1 text-meta text-muted-foreground">
          인기도 40 이상, 난이도 40 이하면 최우선 키워드다. 상위 10개 앱의 평균 평점은 {result.avg_rating.toFixed(1)}, 평균 리뷰는{' '}
          {formatCount(result.avg_reviews)}개다.
        </p>
      </section>

      <StoreOrder result={result} />
      <Autocomplete term={result.term} hints={result.hints} />

      <section aria-label="상위 앱" className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <h2 className="text-section font-semibold">Search API 결과</h2>
          <span className="text-meta text-muted-foreground">순서는 스토어와 다르다. 앱과 평점을 볼 때 쓴다</span>
        </div>
        <DataTable
          label="상위 앱"
          rows={result.top.map((a, i) => ({ ...a, place: i + 1 }))}
          rowKey={(a) => a.trackId}
          columns={TOP_COLUMNS}
          empty={<EmptyState title="이 키워드에 나온 앱이 없다" />}
        />
      </section>
    </div>
  )
}

type StoreRow = StoreApp & { place: number }

const STORE_COLUMNS: Column<StoreRow>[] = [
  {
    key: 'rank',
    header: '순위',
    width: '56px',
    card: 'meta',
    cell: (a) => <span className="font-semibold tabular-nums text-muted-foreground">#{a.place}</span>,
  },
  {
    key: 'app',
    header: '앱',
    card: 'title',
    cell: (a) => (
      <span className="min-w-0">
        <a
          href={`https://apps.apple.com/app/id${a.track_id}`}
          target="_blank"
          rel="noreferrer"
          className="block truncate font-medium hover:text-accent hover:underline"
        >
          {a.name}
        </a>
        {a.subtitle && <span className="block truncate text-meta text-muted-foreground">{a.subtitle}</span>}
      </span>
    ),
  },
  {
    key: 'ratings',
    header: '평점 수',
    width: '88px',
    align: 'right',
    className: 'tabular-nums',
    card: 'line',
    cell: (a) => (a.rating_count > 0 ? formatCount(a.rating_count) : <span className="text-muted-foreground">—</span>),
  },
]

/** The store's own answer. A failed read says so: the Search API's order below
 *  is another list and never stands in for it. */
function StoreOrder({ result }: { result: ResearchResult }) {
  const store = result.store
  return (
    <section aria-label="스토어 순서" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 className="text-section font-semibold">스토어 순서</h2>
        {store && (
          <span className="text-meta tabular-nums text-muted-foreground">
            상위 {store.top.length}개 중 이름에 문구가 있는 앱 {store.name_holders}개 · 평점 수 중앙값 {formatCount(store.median_ratings)}
          </span>
        )}
      </div>
      {store ? (
        <DataTable
          label="스토어 순서"
          rows={store.top.map((a, i) => ({ ...a, place: i + 1 }))}
          rowKey={(a) => a.track_id}
          columns={STORE_COLUMNS}
          empty={<EmptyState title="스토어 결과가 없다" />}
        />
      ) : (
        <p className="text-table text-muted-foreground">스토어를 읽지 못했다. {result.store_error}</p>
      )}
    </section>
  )
}

function Autocomplete({ term, hints }: { term: string; hints?: string[] }) {
  if (!hints) return null
  const self = term.trim().toLowerCase()
  return (
    <section aria-label="자동완성" className="space-y-2">
      <h2 className="text-section font-semibold">자동완성</h2>
      {hints.length === 0 ? (
        <p className="text-table text-muted-foreground">자동완성에 나온 문구가 없다.</p>
      ) : (
        <ol className="flex flex-wrap gap-1.5">
          {hints.map((h) => (
            <li key={h} className={cn('rounded-md border border-border px-2 py-1 text-table', h.toLowerCase() === self && 'border-accent font-medium')}>
              {h}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function Measure({ label, value, fill }: { label: string; value: number; fill: string }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-meta">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      <div role="img" aria-label={`${label} ${value} / 100`} className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', fill)} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
    </div>
  )
}

type TopRow = ITunesResult & { place: number }

const TOP_COLUMNS: Column<TopRow>[] = [
  {
    key: 'rank',
    header: '순위',
    width: '56px',
    card: 'meta',
    cell: (a) => <span className="font-semibold tabular-nums text-muted-foreground">#{a.place}</span>,
  },
  {
    key: 'app',
    header: '앱',
    card: 'title',
    cell: (a) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <AppIcon src={a.artworkUrl100} name={a.trackName} size={32} />
        <span className="min-w-0">
          <a
            href={`https://apps.apple.com/app/id${a.trackId}`}
            target="_blank"
            rel="noreferrer"
            className="block truncate font-medium hover:text-accent hover:underline"
          >
            {a.trackName}
          </a>
          {a.sellerName && <span className="block truncate text-meta text-muted-foreground">{a.sellerName}</span>}
        </span>
      </span>
    ),
  },
  {
    key: 'rating',
    header: '평점',
    width: '80px',
    align: 'right',
    className: 'tabular-nums',
    card: 'line',
    cardHide: (a) => !(a.averageUserRating > 0),
    cell: (a) => (a.averageUserRating > 0 ? a.averageUserRating.toFixed(1) : <span className="text-muted-foreground">—</span>),
  },
  {
    key: 'reviews',
    header: '리뷰',
    width: '88px',
    align: 'right',
    className: 'tabular-nums',
    card: 'line',
    cardHide: (a) => !(a.userRatingCount > 0),
    cell: (a) => (a.userRatingCount > 0 ? formatCount(a.userRatingCount) : <span className="text-muted-foreground">—</span>),
  },
]

/** The bridge from a researched term to a tracked one: pick one of my apps
 *  and add it as a keyword. Adding the same term again changes nothing (the
 *  server upserts). */
function AddToApp({ term, country }: { term: string; country: string }) {
  const toast = useToast()
  const qc = useQueryClient()
  const apps = useApps()
  const mine = useMemo(() => (apps.data?.items ?? []).filter((a) => a.is_mine), [apps.data])
  const [appId, setAppId] = useState<number | ''>('')
  const targetId = appId === '' ? mine[0]?.id : appId

  const add = useMutation({
    mutationFn: (id: number) => api.createKeyword(id, { term, country, source: 'suggestion' }),
    onSuccess: (_k, id) => {
      invalidateKeywordData(qc, id)
      toast.success(`“${term}”을 ${mine.find((a) => a.id === id)?.name ?? '앱'}에 추가했다`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  if (apps.isLoading) return null
  if (mine.length === 0) {
    return (
      <p className="text-table text-muted-foreground">
        추적할 내 앱이 없다.{' '}
        <Link to="/apps" className="text-accent hover:underline">
          앱을 먼저 추가한다
        </Link>
      </p>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <Select
        aria-label="추가할 앱"
        value={targetId ?? ''}
        onChange={(e) => setAppId(Number(e.target.value))}
        className="h-8 w-auto max-w-[12rem] text-table"
      >
        {mine.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Select>
      <Button size="sm" icon={Plus} loading={add.isPending} disabled={!targetId} onClick={() => targetId && add.mutate(targetId)}>
        앱에 추가
      </Button>
    </div>
  )
}
